/**
 * Pure helpers for the per-API benchmark section. Collapses the per-SKU
 * benchmark map (GET /benchmarks) down to one platform-level summary for a
 * detail page's endpoints.
 */
import type { Benchmarks, SkuBenchmark } from '../data/analytics';
import { fmtSec } from './format';
import { catalogText } from '../i18n/catalogText';
import { m } from '../paraglide/messages';

export interface BenchmarkSummary {
  uptimePct: number;
  /** exact uptime population */
  uptimeSample: number;
  latencyP50Ms: number;
  latencyP95Ms: number;
  latencyP99Ms: number;
  /** exact latency population */
  latencySample: number;
  /** true when multiple endpoint percentiles were combined as a weighted
   * estimate rather than recomputed from raw request observations */
  latencyIsEstimate: boolean;
  /** population of admitted executions */
  requests: number;
  providerCount: number;
  /** fallback share of served requests */
  fallbackRatePct: number;
  definitionVersion: number;
  /** any endpoint in this summary takes a caller-requested wait that the numbers
   *  in this summary exclude. */
  excludesCallerDelay: boolean;
  /** the gateway's own wording for that exclusion, carried through unchanged
   *  but for its translation (i18n/catalogText.ts).
   *  Undefined when the gateway did not send it, in which case no footnote is
   *  rendered - the UI never substitutes prose of its own. */
  latencyNote?: string;
}

/** Minimum combined sample before a benchmark figure is worth displaying: show
 *  any real measurement, including smoke and test runs. Only a combined sample
 *  of 0 hides the panel, so it renders a dash rather than collapsing. */
const BENCHMARK_DISPLAY_MIN = 1;

/** Whether one metric has a population worth displaying. */
export function hasBenchmarkPopulation(sample: number): boolean {
  return sample >= BENCHMARK_DISPLAY_MIN;
}

/** Maps current-window measurements by SKU. The response-level `latencyNote`
 *  rides down onto each measurement so the panel that renders a number also
 *  has the gateway's sentence about it. */
export function selectDisplayBenchmarks(data: Benchmarks): Record<string, SkuBenchmark> {
  return Object.fromEntries(data.bySku.map((row) => [
    row.sku,
    {
      ...row,
      definitionVersion: data.definitionVersion,
      latencyNote: data.latencyNote && catalogText(data.latencyNote),
    },
  ]));
}

/**
 * Sample-weighted aggregate of the given SKUs' benchmarks, or null when neither
 * uptime nor latency has a population. Uptime uses uptimeSample and latency
 * uses latencySample.
 *
 * Members with no current-window measurement are excluded and contribute zero.
 */
export function aggregateBenchmarks(
  skus: string[],
  map: Record<string, SkuBenchmark>,
): BenchmarkSummary | null {
  const selected = skus
    .map((s) => map[s])
    .filter((b): b is SkuBenchmark => Boolean(b));
  const requests = selected.reduce((n, row) => n + row.requests, 0);
  const uptimeWeight = (b: SkuBenchmark) => b.uptimeSample;
  const latencyWeight = (b: SkuBenchmark) => b.latencySample;
  const uptimePopulation = selected.reduce((n, b) => n + uptimeWeight(b), 0);
  const latencyPopulation = selected.reduce((n, b) => n + latencyWeight(b), 0);
  const latencyMembers = selected.filter((b) => latencyWeight(b) > 0).length;
  if (
    uptimePopulation < BENCHMARK_DISPLAY_MIN &&
    latencyPopulation < BENCHMARK_DISPLAY_MIN
  ) return null;
  const weighted = (
    pick: (b: SkuBenchmark) => number,
    weight: (b: SkuBenchmark) => number,
    population: number,
  ) => population > 0
    ? selected.reduce((n, b) => n + pick(b) * weight(b), 0) / population
    : 0;
  return {
    uptimePct: weighted((b) => b.uptimePct, uptimeWeight, uptimePopulation),
    uptimeSample: uptimePopulation,
    latencyP50Ms: Math.round(weighted((b) => b.latencyP50Ms, latencyWeight, latencyPopulation)),
    latencyP95Ms: Math.round(weighted((b) => b.latencyP95Ms, latencyWeight, latencyPopulation)),
    latencyP99Ms: Math.round(weighted((b) => b.latencyP99Ms, latencyWeight, latencyPopulation)),
    latencySample: latencyPopulation,
    latencyIsEstimate: latencyMembers > 1,
    requests,
    providerCount: selected.reduce((n, b) => Math.max(n, b.providerCount), 0),
    fallbackRatePct: weighted(
      (b) => b.fallbackRatePct ?? 0,
      latencyWeight,
      latencyPopulation,
    ),
    definitionVersion: selected[0]?.definitionVersion ?? 2,
    excludesCallerDelay: selected.some((b) => b.excludesCallerDelay === true),
    latencyNote: selected.find((b) => b.latencyNote)?.latencyNote,
  };
}

/** How the platform's calls split across the three reliability outcomes, as a
 *  share of ALL measured calls (the three add up to 100). `failoverPct` is the
 *  slice of successes that a fallback lane won; it is 0 when the gateway does
 *  not report `fallbackRatePct`. Drives the Concept A reliability meter. */
export interface ReliabilityBreakdown {
  /** succeeded on the first lane tried */
  firstLanePct: number;
  /** succeeded, but only after failing over to another lane */
  failoverPct: number;
  /** never returned data */
  failedPct: number;
}

export function reliabilityBreakdown(summary: BenchmarkSummary): ReliabilityBreakdown {
  const successPct = Math.max(0, Math.min(100, summary.uptimePct));
  const failedPct = 100 - successPct;
  const failoverPct = successPct * (Math.max(0, summary.fallbackRatePct) / 100);
  const firstLanePct = successPct - failoverPct;
  return { firstLanePct, failoverPct, failedPct };
}

/** Deterministic adaptive axis ceiling with one second of headroom. Because the
 *  scale is `ms + 1s`, every distinct non-negative upper percentile maps to a
 *  distinct track position, including values on exact-second boundaries. */
export function adaptiveLatencyScaleMs(ms: number): number {
  return Math.max(1000, Math.max(0, ms) + 1000);
}

/** The values the latency-range visual needs, in milliseconds against a
 *  0..`scaleMs` axis. `scaleMs` is an adaptive per-page ceiling above the
 *  displayed p99, so the marker is never clipped and each page sizes its own
 *  axis. */
export interface LatencyRange {
  typicalMs: number;         // p50
  upperMs: number;           // p99
  scaleMs: number;           // adaptive ceiling with one-second headroom
}

export function latencyRange(summary: BenchmarkSummary): LatencyRange {
  return {
    typicalMs: summary.latencyP50Ms,
    upperMs: summary.latencyP99Ms,
    scaleMs: adaptiveLatencyScaleMs(Math.max(summary.latencyP50Ms, summary.latencyP99Ms)),
  };
}

export interface LatencyLabels {
  typical: string;        // "Typical (p50)"
  typicalValue: string;   // "4.8s"
  typicalMeaning: string; // "Half of requests finish within 4.8s"
  upper: string;          // "p99 response time"
  upperValue: string;     // "11.8s"
  upperMeaning: string;   // "p99 response time: 99% of requests finish within 11.8s"
}

export function latencyLabels(range: LatencyRange, estimated = false): LatencyLabels {
  if (estimated) {
    return {
      typical: m.measure_latency_weighted_p50(),
      typicalValue: fmtSec(range.typicalMs),
      typicalMeaning: m.measure_latency_weighted_p50_meaning(),
      upper: m.measure_latency_weighted_p99(),
      upperValue: fmtSec(range.upperMs),
      upperMeaning: m.measure_latency_weighted_p99_meaning(),
    };
  }
  return {
    typical: m.measure_latency_typical(),
    typicalValue: fmtSec(range.typicalMs),
    typicalMeaning: m.measure_latency_typical_meaning({ value: fmtSec(range.typicalMs) }),
    upper: m.measure_latency_p99(),
    upperValue: fmtSec(range.upperMs),
    upperMeaning: m.measure_latency_p99_meaning({ value: fmtSec(range.upperMs) }),
  };
}

/** Track geometry for the latency-range chart. Exported so LatencyRangeChart
 *  and its tests share one source of truth (SVG viewBox is 0..560 wide). */
export const LATENCY_TRACK = { x0: 8, width: 544 } as const;

/** Marker x-pixel for `ms` on the track, clamped to the scale. Mirrors the
 *  chart's positioning so tests can assert marker placement and label anchors. */
export function latencyTrackX(ms: number, scaleMs: number): number {
  const { x0, width } = LATENCY_TRACK;
  return x0 + (Math.max(0, Math.min(scaleMs, ms)) / scaleMs) * width;
}
